/**
 * BarChart - a categorical comparison chart with grouped and stacked variants,
 * in vertical (column) and horizontal (bar) orientations.
 *
 * Built on recharts. Accepts arbitrary row shapes, infers its series when they
 * are not declared, and always resolves to a visible state (loading, error,
 * empty, or chart) so it can be dropped straight onto a dashboard without guard
 * code around it.
 *
 * The value axis always includes zero and there is no option to move it. A bar
 * encodes its quantity as a length, so a baseline anywhere else multiplies every
 * difference on the chart by an arbitrary amount. A line may frame its range
 * because it encodes change through position; a bar may not.
 *
 * Usage:
 *   <BarChart data={rows} categoryKey="framework" />
 *   <BarChart variant="stacked" data={rows} categoryKey="month" series={["open", "closed"]} />
 *   <BarChart orientation="horizontal" sort="valueDesc" data={rows} categoryKey="control" />
 */

import { useCallback, useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "../../ui/utils";
import {
  CHART_PALETTE_DARK,
  CHART_PALETTE_LIGHT,
  AXIS_TICK_FONT_SIZE,
  AXIS_TICK_FONT_WEIGHT,
  BAR_FILL,
  GRID_DASH,
  MARK_CORNER_RADIUS,
  MARK_STROKE_WIDTH,
  markGlow,
  formatCompact,
  formatNumber,
  resolveErrorMessage,
  resolvePlotHeight,
  useElementWidth,
  useIsDarkTheme,
  usePrefersReducedMotion,
} from "../chartUtils";
import {
  ChartEmptyState,
  ChartErrorBoundary,
  ChartErrorState,
  ChartLoadingState,
  ChartWarnings,
} from "../ChartStates";
import { BarTooltip, SeriesLegend, SeriesTable } from "./BarChrome";
import {
  hasNegativeValues,
  longestLabelLength,
  normalizeBarData,
  type BarHighlight,
  type BarPoint,
  type BarSort,
  type ChartSeries,
  type SeriesInput,
} from "./barUtils";

/** Hairlines one step off the surface, so the grid never competes with data. */
const GRID_LIGHT = "#e2e8f0";
const GRID_DARK = "rgba(255,255,255,0.10)";
const AXIS_TEXT_LIGHT = "#64748b";
const AXIS_TEXT_DARK = "rgba(255,255,255,0.55)";
/** Wash behind the hovered category. Reads as a highlight, not as a mark. */
const CURSOR_LIGHT = "rgba(15,23,42,0.04)";
const CURSOR_DARK = "rgba(255,255,255,0.05)";

/**
 * Outline-only bars can afford width that filled ones could not: with no fill
 * there is no slab of colour to grow, so the bar reads as a shape at a size
 * that would have been overbearing before.
 */
const MAX_BAR_SIZE = 44;
/** Row pitch a horizontal bar needs before its label starts to crowd. */
const HORIZONTAL_ROW_HEIGHT = 34;

export interface BarChartProps {
  /**
   * DOM id for the chart's root element. Stays on the same element across the
   * loading, error, empty, and plotted states, so anything anchored to it does
   * not lose its target when the state changes. Also seeds the ids of the
   * internal title and plot nodes.
   */
  id?: string;
  /**
   * Identifier for the chart instance, used for tests, analytics, and event
   * handlers. Rendered as `data-name`, since `name` is not a valid attribute on
   * a div, and passed back as the last argument to onBarClick.
   */
  name?: string;

  /** Rows to plot. Null, undefined, and non-arrays resolve to the empty state. */
  data: unknown;
  /**
   * Grouped sets each series beside the others, which compares them directly.
   * Stacked sums them into one bar, which compares totals and costs the reader
   * the ability to compare anything but the bottom segment.
   */
  variant?: "grouped" | "stacked";
  /**
   * Vertical draws columns growing up from the baseline. Horizontal draws bars
   * growing right, which is the honest choice for long category labels and for
   * a ranked list: the labels get a full line each instead of being turned.
   */
  orientation?: "vertical" | "horizontal";

  /** Field holding each row's category label. */
  categoryKey?: string;
  /**
   * Measures to plot. A bare string is shorthand for the data key. Omitted, the
   * numeric fields in the data are inferred, in first-seen order.
   */
  series?: SeriesInput[];

  title?: string;
  subtitle?: string;

  /**
   * Plot height in px, excluding the header, legend, and table. Horizontal
   * charts size themselves to their row count when this is not set.
   */
  height?: number;
  /** Replaces the built-in categorical hues. Assigned in order, never cycled. */
  palette?: readonly string[];

  loading?: boolean;
  /** Fetch failure. Takes precedence over every other state. */
  error?: string | Error | null;
  /** Retry handler shown on the error state. */
  onRetry?: () => void;
  emptyMessage?: string;

  /** Series past this cap are dropped and named in a warning. */
  maxSeries?: number;
  /** Categories past this cap are dropped and counted in a warning. */
  maxCategories?: number;
  /** Category order. Value sorting ranks by each category's total. */
  sort?: BarSort;

  showLegend?: boolean;
  legendPosition?: "top" | "bottom";
  /** Allow clicking legend items to hide and restore series. */
  legendToggle?: boolean;

  showGrid?: boolean;
  /** The category axis: x when vertical, y when horizontal. */
  showCategoryAxis?: boolean;
  /** The value axis: y when vertical, x when horizontal. */
  showValueAxis?: boolean;

  /**
   * Values written at the end of each bar. Defaults to automatic: on for a
   * single series over a short category list, where they replace the axis, and
   * off otherwise, since a number on every bar of a grouped chart is unreadable.
   * Never applied to stacked segments, which have no free end to write on.
   */
  showValueLabels?: boolean;

  showTooltip?: boolean;
  /** Expandable text equivalent of the chart. */
  showDataTable?: boolean;
  /** Surface notes about dropped rows, missing values, and uncharted series. */
  showWarnings?: boolean;

  /** Suffix appended to every rendered value, for example "%" or " ms". */
  unit?: string;
  valueFormatter?: (value: number) => string;
  /** Formats category tick labels, for example shortening a date. */
  categoryFormatter?: (value: string) => string;
  barHighlight?: (point: BarPoint) => BarHighlight | undefined;

  onBarClick?: (point: BarPoint, seriesId: string, index: number, name?: string) => void;

  className?: string;
  /** Overrides the generated chart description read by screen readers. */
  ariaLabel?: string;
}

interface BarChartHeaderProps {
  readonly title?: string;
  readonly subtitle?: string;
  readonly id?: string;
}

function BarChartHeader({ title, subtitle, id }: Readonly<BarChartHeaderProps>) {
  if (!title && !subtitle) return null;
  return (
    <div className="mb-3 min-w-0">
      {title && (
        <h3
          id={id ? `${id}-title` : undefined}
          className="break-words text-sm font-bold leading-snug text-foreground"
        >
          {title}
        </h3>
      )}
      {subtitle && (
        <p className="mt-0.5 break-words text-xs leading-snug text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );
}

function getBarRadius(
  isStacked: boolean,
  isHorizontal: boolean,
  index: number,
  totalSeries: number,
): [number, number, number, number] {
  const roundedEnd = !isStacked || index === totalSeries - 1;
  if (!roundedEnd) return [0, 0, 0, 0];
  if (isHorizontal) return [0, MARK_CORNER_RADIUS, MARK_CORNER_RADIUS, 0];
  return [MARK_CORNER_RADIUS, MARK_CORNER_RADIUS, 0, 0];
}
function buildSeriesDescriptionParts(
  points: BarPoint[],
  visibleSeries: ChartSeries[],
  unit: string,
  formatValue: (value: number) => string,
): string {
  return visibleSeries
    .map((entry) => {
      const values = points
        .map((point) => point[entry.key])
        .filter((value): value is number => typeof value === "number");
      if (!values.length) return `${entry.label} has no values`;
      const max = Math.max(...values);
      const min = Math.min(...values);
      const top = points.find((point) => point[entry.key] === max);
      return `${entry.label} ranges from ${formatValue(min)}${unit} to ${formatValue(max)}${unit}, highest at ${top?.x}`;
    })
    .join(". ");
}

function resolveBarPlotHeight(
  height: number | undefined,
  isHorizontal: boolean,
  pointCount: number,
): number {
  const fallbackHeight = isHorizontal
    ? Math.min(560, Math.max(160, pointCount * HORIZONTAL_ROW_HEIGHT + 28))
    : 260;
  return resolvePlotHeight(height, fallbackHeight);
}
function BarChartInner({
  id,
  name,
  data,
  variant = "grouped",
  orientation = "vertical",
  categoryKey = "name",
  series: seriesInput,
  title,
  subtitle,
  height,
  palette,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = "There are no records to chart for the current selection.",
  maxSeries = 8,
  maxCategories = 24,
  sort = "none",
  showLegend = true,
  legendPosition = "top",
  legendToggle = true,
  showGrid = true,
  showCategoryAxis = true,
  showValueAxis = true,
  showValueLabels,
  showTooltip = true,
  showDataTable = false,
  showWarnings = true,
  unit = "",
  valueFormatter,
  categoryFormatter,
  barHighlight,
  onBarClick,
  className,
  ariaLabel,
}: Readonly<BarChartProps>) {
  const isDark = useIsDarkTheme();
  const reduceMotion = usePrefersReducedMotion();
  const [containerRef, containerWidth] = useElementWidth<HTMLDivElement>();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const [tableOpen, setTableOpen] = useState(false);

  const isStacked = variant === "stacked";
  const isHorizontal = orientation === "horizontal";

  const activePalette = useMemo(() => {
    const source = palette?.filter?.(Boolean);
    if (source?.length) return source;
    return isDark ? CHART_PALETTE_DARK : CHART_PALETTE_LIGHT;
  }, [palette, isDark]);

  const formatValue = useCallback(
    (value: number) => {
      if (typeof valueFormatter === "function") {
        try {
          return valueFormatter(value);
        } catch {
          // A throwing formatter must not take the chart down with it.
          return formatNumber(value);
        }
      }
      return formatNumber(value);
    },
    [valueFormatter],
  );

  const formatCategory = useCallback(
    (value: string) => {
      if (typeof categoryFormatter !== "function") return value;
      try {
        return categoryFormatter(value);
      } catch {
        return value;
      }
    },
    [categoryFormatter],
  );

  const { points, series, warnings } = useMemo(
    () =>
      normalizeBarData(data, {
        categoryKey,
        series: seriesInput,
        palette: activePalette,
        maxSeries,
        maxCategories,
        sort,
        stacked: isStacked,
      }),
    [data, categoryKey, seriesInput, activePalette, maxSeries, maxCategories, sort, isStacked],
  );

  // Hidden series leave the plot but keep their assigned colour, so restoring
  // one does not repaint the survivors.
  const visibleSeries = useMemo(
    () => series.filter((entry) => !hiddenIds.has(entry.id)),
    [series, hiddenIds],
  );

  const handleToggle = useCallback(
    (seriesId: string) => {
      if (!legendToggle) return;
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (next.has(seriesId)) {
          next.delete(seriesId);
          return next;
        }
        // Refuse the toggle that would empty the chart.
        if (prev.size + 1 >= series.length) return prev;
        next.add(seriesId);
        return next;
      });
    },
    [legendToggle, series.length],
  );

  const handleBarClick = useCallback(
    (entry: ChartSeries, index: number) => {
      const point = points?.[index];
      // `name` rides along so one handler can serve several charts.
      if (point && typeof onBarClick === "function") onBarClick(point, entry.id, index, name);
    },
    [points, onBarClick, name],
  );

  const negatives = useMemo(
    () => hasNegativeValues(points, visibleSeries),
    [points, visibleSeries],
  );

  // Once a stack has more than one segment it is a stack: the segments in the
  // middle have no free end, so a direct label there would be clipped.
  const stackedSegments = isStacked && visibleSeries.length > 1;

  // Direct labels earn their place when there is one number per category and
  // room to write it. On a grouped chart they become a wall of digits, and
  // inside a stacked segment they would be clipped by their own mark.
  const valueLabelsOn =
    !stackedSegments &&
    (showValueLabels ?? (visibleSeries.length === 1 && points.length <= 12));

  const description = useMemo(() => {
    if (ariaLabel) return ariaLabel;
    const kind = isStacked ? "Stacked bar" : "Bar";
    const base = title ? `${kind} chart: ${title}.` : `${kind} chart.`;
    if (!points.length || !visibleSeries.length) return `${base} No data.`;
    const parts = buildSeriesDescriptionParts(points, visibleSeries, unit, formatValue);
    return `${base} ${points.length} categories. ${parts}.`;
  }, [ariaLabel, isStacked, title, points, visibleSeries, unit, formatValue]);

  const errorMessage = resolveErrorMessage(error);
  const gridColor = isDark ? GRID_DARK : GRID_LIGHT;
  const axisTextColor = isDark ? AXIS_TEXT_DARK : AXIS_TEXT_LIGHT;
  // Below this the axis labels crowd into each other.
  const compactAxis = containerWidth > 0 && containerWidth < 380;

  const plotHeight = resolveBarPlotHeight(height, isHorizontal, points.length);

  // Long category names need a real gutter, but not an unbounded one: past this
  // the labels would take the space the bars are meant to fill.
  const categoryAxisWidth = useMemo(() => {
    if (!isHorizontal) return undefined;
    const chars = longestLabelLength(points);
    return Math.min(compactAxis ? 96 : 160, Math.max(56, chars * 6.5 + 14));
  }, [isHorizontal, points, compactAxis]);

  const header = <BarChartHeader title={title} subtitle={subtitle} id={id} />;

  const legend = showLegend && series.length > 1 && (
    <SeriesLegend
      series={series}
      activeId={activeId}
      hiddenIds={hiddenIds}
      interactive={legendToggle}
      swatch="bar"
      onActivate={setActiveId}
      onToggle={handleToggle}
    />
  );

  /* Error and loading short-circuit the plot but keep the header in place, so
     the surrounding layout never jumps or blanks between states. */
  if (errorMessage) {
    return (
      <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
        {header}
        <ChartErrorState height={plotHeight} message={errorMessage} onRetry={onRetry} />
      </div>
    );
  }

  if (loading) {
    return (
      <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
        {header}
        <ChartLoadingState height={plotHeight} shape="cartesian" />
      </div>
    );
  }

  if (!points.length || !visibleSeries.length) {
    return (
      <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
        {header}
        <ChartEmptyState height={plotHeight} message={emptyMessage} shape="cartesian" />
        {showWarnings && <ChartWarnings warnings={warnings} />}
      </div>
    );
  }

  /* The value axis always spans zero. Framing a bar chart on its data range
     would scale every difference by an arbitrary factor. */
  const valueDomain: [(min: number) => number, (max: number) => number] = [
    (min: number) => Math.min(0, min),
    (max: number) => Math.max(0, max),
  ];

  const axisTick = {
    fill: axisTextColor,
    fontSize: AXIS_TICK_FONT_SIZE,
    fontWeight: AXIS_TICK_FONT_WEIGHT,
  };

  const categoryAxisProps = {
    dataKey: "x",
    type: "category" as const,
    tick: axisTick,
    tickFormatter: formatCategory,
    axisLine: { stroke: gridColor },
    tickLine: false,
  };

  const valueAxisProps = {
    type: "number" as const,
    tick: axisTick,
    // Compact ticks keep the axis narrow without hiding magnitude.
    tickFormatter: (value: number) => formatCompact(value),
    axisLine: false,
    tickLine: false,
    domain: valueDomain,
  };


  const highlightFor = (point: BarPoint): BarHighlight | undefined => {
    if (typeof barHighlight !== "function") return undefined;
    try {
      return barHighlight(point) ?? undefined;
    } catch {
      return undefined;
    }
  };

  return (
    <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
      {header}

      {legendPosition === "top" && legend && <div className="mb-2">{legend}</div>}

      <div
        id={id ? `${id}-plot` : undefined}
        className="w-full"
        style={{ height: plotHeight }}
        role="img"
        aria-label={description}
        // Per-bar handlers get skipped on fast exits and across the category
        // gaps, which leaves the hover emphasis stuck on the last bar.
        onMouseLeave={() => setHoveredKey(null)}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={points}
            layout={isHorizontal ? "vertical" : "horizontal"}
            margin={{
              top: valueLabelsOn && !isHorizontal ? 20 : 8,
              right: valueLabelsOn && isHorizontal ? 44 : 8,
              left: 0,
              bottom: 0,
            }}
            // The 2px surface gap that separates neighbouring bars. Wider gaps
            // between categories than within a group, so a group reads as one.
            barGap={2}
            barCategoryGap={isStacked ? "28%" : "22%"}
            maxBarSize={MAX_BAR_SIZE}
          >
            {showGrid && (
              // Dashed hairlines, and only across the value axis: a gridline
              // parallel to the bars measures nothing, and dashes keep the
              // grid reading as reference rather than as data.
              <CartesianGrid
                stroke={gridColor}
                strokeWidth={1}
                strokeDasharray={GRID_DASH}
                horizontal={!isHorizontal}
                vertical={isHorizontal}
              />
            )}

            {isHorizontal ? (
              <>
                {showValueAxis && <XAxis {...valueAxisProps} height={28} />}
                {showCategoryAxis && (
                  <YAxis {...categoryAxisProps} width={categoryAxisWidth} interval={0} />
                )}
              </>
            ) : (
              <>
                {showCategoryAxis && (
                  <XAxis
                    {...categoryAxisProps}
                    height={28}
                    minTickGap={compactAxis ? 16 : 8}
                    // Every bar keeps its label while they still fit. Past that
                    // the ticks thin out and the tooltip and table carry the rest.
                    interval={points.length <= 12 ? 0 : "preserveStartEnd"}
                  />
                )}
                {showValueAxis && <YAxis {...valueAxisProps} width={compactAxis ? 36 : 46} />}
              </>
            )}

            {/* With values on both sides of zero the baseline is a real
                boundary, so it is drawn rather than left implied. */}
            {negatives && (
              <ReferenceLine
                {...(isHorizontal ? { x: 0 } : { y: 0 })}
                stroke={axisTextColor}
                strokeWidth={1}
              />
            )}

            {showTooltip && (
              <Tooltip
                content={
                  <BarTooltip
                    series={visibleSeries}
                    unit={unit}
                    showTotal={isStacked}
                    activeKey={hoveredKey}
                    valueFormatter={formatValue}

                    barHighlight={
                      typeof barHighlight === "function" ? highlightFor : undefined
                    }
                  />
                }
                // A wash behind the whole category, so the hit target is the
                // band rather than the bar itself.
                cursor={{ fill: isDark ? CURSOR_DARK : CURSOR_LIGHT }}
                wrapperStyle={{ outline: "none", zIndex: 20 }}
                isAnimationActive={!reduceMotion}
              />
            )}

            {visibleSeries.map((entry, index) => {
              const dimmed = activeId != null && activeId !== entry.id;
              const radius = getBarRadius(isStacked, isHorizontal, index, visibleSeries.length);

              return (
                <Bar
                  key={entry.id}
                  dataKey={entry.key}
                  name={entry.label}
                  stackId={isStacked ? "stack" : undefined}
                  // Outline-only: no fill, just the stroke in the series hue
                  // plus a soft glow of the same colour. The bar is drawn as a
                  // shape rather than a block, so a tall bar never becomes a
                  // slab of colour and a short one still has a definite edge.
                  fill={BAR_FILL}
                  radius={radius}
                  stroke={entry.color}
                  strokeWidth={MARK_STROKE_WIDTH}
                  opacity={dimmed ? 0.25 : 1}
                  isAnimationActive={!reduceMotion}
                  animationDuration={600}
                  onMouseEnter={() => setHoveredKey(entry.key)}
                  onClick={(_, barIndex: number) => handleBarClick(entry, barIndex)}
                  className={onBarClick ? "cursor-pointer" : undefined}
                >
                  {/* The glow rides on the rectangles, NOT on the <Bar> group.
                      An SVG filter renders the element and every descendant
                      through itself, so a filter up here would also smear the
                      value labels below -- and a child cannot opt out of an
                      ancestor's filter. Per-point Cells keep it on the mark. */}
                  {points.map((point, pointIndex) => {
                    const stroke =
                      highlightFor(point)?.stroke || entry.color;
                    return (
                      <Cell
                        key={`${entry.id}-${point.x}-${pointIndex}`}
                        stroke={stroke}
                        style={{ filter: markGlow(stroke) }}
                      />
                    );
                  })}

                  {valueLabelsOn && (
                    <LabelList
                      dataKey={entry.key}
                      position={isHorizontal ? "right" : "top"}
                      offset={6}
                      // Text wears ink, never the series colour: a light hue is
                      // illegible as type on the card surface.
                      fill={axisTextColor}
                      fontSize={11}
                      fontWeight={700}
                      formatter={(value: unknown) =>
                        typeof value === "number" && Number.isFinite(value)
                          ? `${formatCompact(value)}${unit}`
                          : ""
                      }
                    />
                  )}
                </Bar>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {legendPosition === "bottom" && legend && <div className="mt-2">{legend}</div>}

      {showWarnings && <ChartWarnings warnings={warnings} />}

      {showDataTable && (
        <SeriesTable
          points={points}
          series={visibleSeries}
          unit={unit}
          xHeader={categoryKey === "name" ? "Category" : categoryKey}
          open={tableOpen}
          valueFormatter={formatValue}
          onToggle={() => setTableOpen((open) => !open)}
        />
      )}
    </div>
  );
}

/**
 * Public entry point. Wraps the chart in a local error boundary so a render
 * crash stays inside the chart box instead of reaching the app-level boundary
 * and blanking the page.
 */
export function BarChart(props: Readonly<BarChartProps>) {
  const plotHeight = resolvePlotHeight(props?.height, 260);

  return (
    <ChartErrorBoundary height={plotHeight} onRetry={props?.onRetry}>
      <BarChartInner {...props} />
    </ChartErrorBoundary>
  );
}

export default BarChart;

export type { BarHighlight, BarPoint, BarSort, ChartSeries, SeriesInput };
